import React from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useForm } from 'react-hook-form';
import BaseTextField from 'common/components/base-text-field';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FormProvider from 'common/components/form-provider';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import { forgotPasswordSchema } from 'common/schema';
import { forgotPassword } from 'services/auth.service';
import { IForgotPasswordSchema } from 'types/auth';
import { toast } from 'react-toastify';
import AuthNotification from '../components/auth-guide-dialog';
import image from 'assets/images/backgrounds/bg-forgot-password.jpg';
import Logo from 'common/components/logo';
import { common } from '@mui/material/colors';
import RequiredLabel from 'common/components/required-label';

const ForgotPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispath = useAppDispatch();
    const methods = useForm({
        resolver: yupResolver(forgotPasswordSchema),
        defaultValues: {
            email: '',
        },
    });
    const { handleSubmit, setError } = methods;
    const [openDialogSuccess, setOpenDialogSuccess] =
        React.useState<boolean>(false);
    const onSubmit = handleSubmit(async (data) => {
        dispath(setIsLoading(true));
        const { email } = data as IForgotPasswordSchema;
        const dataForm = {
            email: email,
        };
        try {
            const response = await forgotPassword(dataForm);
            if (response.success) {
                dispath(setIsLoading(false));
                setOpenDialogSuccess(true);
                toast.success(response.message);
            } else {
                Object.entries(response.message).forEach(([key, value]) => {
                    setError(key as keyof IForgotPasswordSchema, {
                        type: 'manual',
                        message: value,
                    });
                });
                dispath(setIsLoading(false));
            }
        } catch (error) {
            dispath(setIsLoading(false));
            if (error instanceof Error) {
                toast.error(error.message);
            }
        }
    });

    return (
        <Box sx={styles.layout}>
            <Box
                sx={{
                    flex: 2,
                    backgroundImage: `url(${image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'right',
                    backgroundRepeat: 'no-repeat',
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: common.white,
                }}
            >
                <Box padding={6}>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <Logo width={90} height={70} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Typography variant="h3" sx={styles.screenName}>
                                    {t('page.forgot.password.title')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Stack marginBottom={'16px'} spacing={'7px'}>
                                    <RequiredLabel
                                        label={t('common.enter.email')}
                                        sx={{ fontSize: '14px', marginLeft: 2 }}
                                        required={false}
                                    />
                                    <BaseTextField
                                        autoComplete="off"
                                        name="email"
                                        fullWidth
                                        size="small"
                                        placeholder={`${t('common.enter.email')}`}
                                        sx={styles.styleInput}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={12} marginBottom={'28px'}>
                                <Button
                                    sx={styles.buttonContinue}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('common.continue')}
                                </Button>
                            </Grid>

                            <Grid item xs={12} md={12} textAlign={'center'}>
                                <Typography
                                    sx={styles.textLink}
                                    component={'a'}
                                    onClick={() => navigate('/login')}
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: t('common.back.to.login'),
                                        }}
                                    />
                                </Typography>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Box>
                {openDialogSuccess && (
                    <AuthNotification
                        title={`Bạn đã yêu cầu lấy lại mật khẩu trên SurveyTrue. \nVui lòng kiểm tra email của bạn để lấy lại mật khẩu mới.`}
                        guideType="forgotPassword"
                        open={openDialogSuccess}
                        onClose={() => {
                            setOpenDialogSuccess(false);
                            navigate('/login');
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};
const styles = createStyleSheet({
    layout: {
        width: '100vw',
        minHeight: '100vh',

        overflowY: 'auto',
        backgroundAttachment: 'fixed',
        display: 'flex',
    },
    screenName: {
        fontSize: '20px',
        fontWeight: 'bold',
        marginTop: '48px',
        marginBottom: '24px',
    },
    textLink: {
        cursor: 'pointer',
        fontSize: '14px',
    },
    buttonContinue: {
        textTransform: 'none',
        height: 42,
        borderRadius: '6px',
    },
    styleInput: {
        '& .MuiInputBase-root': {
            height: '48px',
            fontSize: '16px',
            borderRadius: '6px',
        },
    },
});
export default ForgotPassword;
